/**
 * Which HTTP statuses must NOT be retried by the RTK Query base query.
 *
 * Lives in its own module, free of browser globals, so the guard test can
 * import it under the `node` environment and make a real loopback round trip.
 * Importing `baseApi` there is not an option: it constructs a live `createApi`
 * at module scope and reaches MSAL, so it throws `window is not defined`.
 *
 * 409 (conflict) is a deterministic business rejection (a unique/singleton
 * conflict, or "cancel the reservation first"). Retrying re-fires the same
 * rejection and multiplies server hits for zero benefit. Fail fast.
 *
 * 429 (rate limited) is deliberately included: the server's standard limiter
 * uses a fixed 60s window, so RTK's sub-2s exponential backoff re-fires inside
 * the SAME window and just re-trips the limit, turning one over-limit request
 * into up to 3 server hits and amplifying the burst. Fail fast instead.
 *
 * 304 (not modified) is a CACHE answer, not a failure — but `fetchBaseQuery`
 * surfaces it as an ERROR result (`{status: 304, data: null}`), never as data.
 * While 304 was absent from this list the retry guard
 * (`typeof status === "number" && includes(status)`) let it through: measured
 * at 3 server hits over ~1.7s, settling REJECTED with `data` undefined for the
 * life of that cache entry, so anything keyed on `data` rendered its empty
 * state permanently. Re-firing an identical conditional request can only
 * produce the same 304, so the retries were pure waste.
 *
 * The server-side companion is `Cache-Control: no-store` on /api/v1
 * (server/config/routes.ts). That stops a compliant browser STORING the
 * response, so it never sends the conditional request — but it does NOT stop
 * the server answering 304 to one arriving from a proxy or a hand-set header
 * (measured: Express still emits a weak ETag and still honours `If-None-Match`
 * under no-store). So this entry is load-bearing, not decorative: the header
 * removes the common cause, this handles whatever still arrives.
 */
export const NON_RETRYABLE_STATUS_CODES = [304, 400, 401, 403, 404, 409, 422, 429, 500];
